import { useMutation } from "@tanstack/react-query";
import { useApiConfig } from "@visioni/a3s-client-api/app";
import type { AssetCameraKey, SiteId } from "@visioni/a3s-schema";
import { triggerTestSignalMutation } from "./commands";
import type { TestSignalPath, TriggerTestSignalResponse } from "../client-messages/test-signals";

export interface TriggerTestSignalVariables {
  siteId: string;
  cameraKey: string;
  signalPath: TestSignalPath;
}

/**
 * Fires an on-demand test signal at one camera. Support-only — the API answers
 * 403 for anyone else, and for a camera outside the rollout flag.
 *
 * A resolved mutation means IoT Core accepted the publish, not that the camera
 * produced a signal. Callers must not report success as "a test signal fired".
 */
export function useTriggerTestSignal() {
  const apiConfig = useApiConfig();

  const mutationConfig =
    apiConfig.tenant ?
      {
        tenantId: apiConfig.tenant.tenantId,
        apiBaseUrl: apiConfig.tenant.apiBaseUrl,
        authenticatedFetch: apiConfig.authenticatedFetch,
      }
    : { authenticatedFetch: apiConfig.authenticatedFetch };

  return useMutation<TriggerTestSignalResponse, Error, TriggerTestSignalVariables>({
    mutationFn: ({ siteId, cameraKey, signalPath }) => {
      const { mutationFn } = triggerTestSignalMutation(mutationConfig, {
        siteId: siteId as SiteId,
        assetCameraKey: cameraKey as AssetCameraKey,
      });
      return mutationFn({ signalPath });
    },
  });
}
